/* eslint-disable no-restricted-syntax */
/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { useEffect } from "react";
import type { RefObject } from "react";

import { Log } from "../../viewModel/logging";

const TAG: string = "A11yFocusSync";

/**
 * Mirrors this app's Redux/state-driven D-pad focus onto REAL DOM focus, so
 * Vega VoiceView (which reads the browser accessibility tree) announces the
 * element the user has selected.
 *
 *   focus state (e.g. focusZone / focusedIndex)
 *        ↓
 *   this hook → element.focus({ preventScroll: true })
 *        ↓
 *   Chromium accessibility tree (focused node: role + accessible name)
 *        ↓
 *   VoiceView: "<name>, button"
 *
 * This is the pattern validated in the vega-voiceview-poc. Two rules from it
 * matter:
 *
 * 1. Focus moves in ONE step, A → B. This hook never calls `blur()`. Blurring
 *    the old element before the new one is focused (React runs sibling
 *    effects in tree order) sends focus to <body> first on every
 *    RIGHT/DOWN move. VoiceView then gets two focus events (document, then
 *    button) and the second one can be dropped.
 * 2. Nothing else speaks. There is no speechSynthesis and no aria-live echo
 *    of the focused label; the focused element's own name is what VoiceView
 *    reads.
 *
 * The visible `--focused` CSS modifiers remain the source of truth for what
 * is highlighted; this hook only keeps DOM focus in step with them.
 *
 * @param elementRef - Ref to the focusable element (a <button>, or an element
 *   with role + tabIndex).
 * @param isFocused - True when this element is the current focus target.
 */
export function useAccessibilityFocusSync<T extends HTMLElement>(
  elementRef: RefObject<T | null>,
  isFocused: boolean
): void {
  useEffect((): void => {
    if (!isFocused) {
      return;
    }
    const element: T | null = elementRef.current;
    if (element === null || document.activeElement === element) {
      return;
    }
    element.focus({ preventScroll: true });
    // INFO so it shows in the device console without changing log level.
    // ok=false means the browser refused focus (element hidden, inert,
    // disabled or display:none at that moment), so VoiceView has nothing new
    // to announce.
    Log.i(
      TAG,
      "focus -> " +
        (element.getAttribute("aria-label") ??
          element.textContent?.trim() ??
          element.tagName) +
        " | ok=" +
        String(document.activeElement === element) +
        " | aria-hidden ancestor=" +
        String(element.closest("[aria-hidden='true']") !== null)
    );
  }, [elementRef, isFocused]);
}
